/**
 * @class SoulogShowcaseScroll
 * Card gallery showcase (pure JS, no GSAP).
 *
 * Desktop (hover + fine pointer): cards are laid out as a coverflow and switch one by one as the page scrolls within the sticky stage;
 *   click the center card → it enlarges to fill the screen and the rest shrink to thumbnails in the bottom-right; scroll once more in the enlarged state → restore the gallery and advance to the next card.
 * Mobile (touch): cards stack vertically and follow page scroll, fading in as they enter the viewport; tap a card → fullscreen, tap ✕ / tap again to collapse.
 * prefers-reduced-motion: transitions off, content visible immediately.
 * No JS: [data-ready] is not added, and CSS keeps the cards in normal document flow, always visible.
 * Shopify compatibility: scoped to the section root, re-initializes in connectedCallback, cleans up in disconnectedCallback.
 */
if (!customElements.get("soulog-showcase-scroll")) {
	const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

	class SoulogShowcaseScroll extends HTMLElement {
		connectedCallback() {
			this.wrap = this.querySelector(".ssc__wrap");
			this.stage = this.querySelector(".ssc__stage");
			this.deck = this.querySelector(".ssc__deck");
			this.dotsWrap = this.querySelector(".ssc__dots");
			this.focusClose = this.querySelector(".ssc__focus-close");
			this.fsClose = this.querySelector(".ssc__fs-close");
			this.cards = Array.from(this.querySelectorAll(".ssc__card"));
			this.N = this.cards.length;

			// Must include a width gate: with only hover/pointer checks, the Shopify editor's mobile preview (narrow viewport + desktop mouse pointer)
			// wrongly matches the desktop coverflow and renders broken on narrow screens. With min-width:1024px added, narrow screens always take the static vertical mobile layout.
			this.desktopMq = window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)");
			this.reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");

			this.index = 0;
			this.focused = false;
			this.fsCard = null;
			this.scrollLock = false;
			this.ticking = false;
			this.io = null;

			this.onScroll = () => this.requestUpdate();
			this.onResize = () => this.layout(true);
			this.onModeChange = () => this.setup();

			if (this.desktopMq.addEventListener) this.desktopMq.addEventListener("change", this.onModeChange);
			else if (this.desktopMq.addListener) this.desktopMq.addListener(this.onModeChange);

			this.setAttribute("data-ready", "");
			this.setup();
		}

		disconnectedCallback() {
			this.teardown();
			if (this.desktopMq) {
				if (this.desktopMq.removeEventListener) this.desktopMq.removeEventListener("change", this.onModeChange);
				else if (this.desktopMq.removeListener) this.desktopMq.removeListener(this.onModeChange);
			}
		}

		setup() {
			this.teardown();
			if (this.N === 0) return;
			this.desktopMq.matches ? this.setupDesktop() : this.setupMobile();
		}

		/* ===================== Desktop ===================== */
		setupDesktop() {
			this.index = 0;
			this.focused = false;

			// Progress dots
			this.dotsWrap.innerHTML = "";
			this.cards.forEach((_, i) => {
				const b = document.createElement("button");
				b.className = "ssc__dot" + (i === 0 ? " is-active" : "");
				b.type = "button";
				b.setAttribute("aria-label", "Slide " + (i + 1));
				b.addEventListener("click", () => this.scrollToIndex(i));
				this.dotsWrap.appendChild(b);
			});
			this.dots = Array.from(this.dotsWrap.children);

			this.cardClick = this.cards.map((card) => {
				const h = () => {
					const i = Number(card.dataset.index);
					if (!this.focused) this.enterFocus(i);
					else if (i !== this.index) this.setIndex(i);
				};
				card.addEventListener("click", h);
				return h;
			});

			this.onClose = () => this.exitFocus(false);
			this.focusClose.addEventListener("click", this.onClose);

			let cooldown = false;
			this.onWheel = (e) => {
				if (!this.focused) return;
				e.preventDefault();
				if (cooldown || Math.abs(e.deltaY) < 6) return;
				cooldown = true;
				setTimeout(() => (cooldown = false), 700);
				this.exitFocus(e.deltaY > 0);
			};
			this.onKey = (e) => {
				if (e.key === "Escape" && this.focused) this.exitFocus(false);
				else if (!this.focused) {
					if (e.key === "ArrowRight") this.scrollToIndex(this.index + 1);
					else if (e.key === "ArrowLeft") this.scrollToIndex(this.index - 1);
					else if (e.key === "Enter") this.enterFocus(this.index);
				}
			};

			window.addEventListener("scroll", this.onScroll, { passive: true });
			window.addEventListener("resize", this.onResize);
			window.addEventListener("wheel", this.onWheel, { passive: false });
			window.addEventListener("keydown", this.onKey);
			this._desktop = true;

			this.layout(true);
			this.computeIndex();
		}

		targetFor(i) {
			const card = this.cards[i];
			const offset = i - this.index;
			const t = (x, y, xp, s, r, o, z) => ({
				transform: `translateX(${xp}%) translate(${x}px, ${y}px) scale(${s}) rotateY(${r}deg)`,
				opacity: o,
				z,
			});
			if (this.focused) {
				if (i === this.index) {
					const s = Math.min(
						(window.innerWidth * 0.92) / card.offsetWidth,
						(window.innerHeight * 0.82) / card.offsetHeight,
						1.6,
					);
					return t(0, 0, 0, s, 0, 1, 60);
				}
				const dockOrder = i < this.index ? i : i - 1;
				const w = card.offsetWidth;
				const h = card.offsetHeight;
				const s = 200 / w;
				const stackH = h * s + 12;
				const x = window.innerWidth / 2 - 24 - (w * s) / 2;
				const y = window.innerHeight / 2 - 24 - (h * s) / 2 - dockOrder * stackH;
				return t(x, y, 0, s, 0, 1, 40 - dockOrder);
			}
			if (offset === 0) return t(0, 0, 0, 1, 0, 1, 30);
			if (Math.abs(offset) === 1) {
				const dir = offset > 0 ? 1 : -1;
				return t(0, 0, dir * 62, 0.8, -dir * 24, 0.45, 20);
			}
			const dir = offset > 0 ? 1 : -1;
			return t(0, 0, dir * 120, 0.7, -dir * 24, 0, 10);
		}

		layout(instant) {
			if (!this._desktop) return;
			if (instant || this.reduceMq.matches) this.classList.add("ssc--no-anim");
			this.cards.forEach((card, i) => {
				card.classList.toggle("is-dock", this.focused && i !== this.index);
				const tf = this.targetFor(i);
				card.style.transform = tf.transform;
				card.style.opacity = tf.opacity;
				card.style.zIndex = tf.z;
			});
			if (this.dots) this.dots.forEach((d, i) => d.classList.toggle("is-active", i === this.index));
			this.classList.toggle("is-focused", this.focused);
			this.playVisible();
			if (instant || this.reduceMq.matches) {
				void this.offsetWidth; // force reflow to swallow the transition
				this.classList.remove("ssc--no-anim");
			}
		}

		playVisible() {
			this.cards.forEach((card, i) => {
				const v = card.querySelector("video");
				if (!v) return;
				const play = this.focused ? true : i === this.index;
				if (play) {
					const p = v.play();
					if (p && p.catch) p.catch(() => {});
				} else {
					v.pause();
				}
			});
		}

		setIndex(i) {
			i = clamp(i, 0, this.N - 1);
			if (i === this.index) return;
			this.index = i;
			this.layout(false);
		}

		requestUpdate() {
			if (this.ticking) return;
			this.ticking = true;
			requestAnimationFrame(() => {
				this.ticking = false;
				this.computeIndex();
			});
		}

		computeIndex() {
			if (!this._desktop || this.focused || this.scrollLock || this.N < 2) return;
			const rect = this.wrap.getBoundingClientRect();
			const scrollable = rect.height - window.innerHeight;
			const progress = scrollable > 0 ? clamp(-rect.top / scrollable, 0, 1) : 0;
			this.setIndex(Math.round(progress * (this.N - 1)));
		}

		scrollToIndex(i) {
			if (this.N < 2) return;
			i = clamp(i, 0, this.N - 1);
			const rect = this.wrap.getBoundingClientRect();
			const wrapTop = rect.top + window.scrollY;
			const scrollable = rect.height - window.innerHeight;
			const y = wrapTop + (i / (this.N - 1)) * scrollable;
			this.scrollLock = true;
			this.setIndex(i);
			window.scrollTo({ top: y, behavior: this.reduceMq.matches ? "auto" : "smooth" });
			clearTimeout(this._lockTimer);
			this._lockTimer = setTimeout(() => (this.scrollLock = false), 700);
		}

		enterFocus(i) {
			this.setIndex(i);
			this.focused = true;
			this.layout(false);
		}

		exitFocus(advance) {
			this.focused = false;
			this.layout(false);
			if (advance && this.index < this.N - 1) this.scrollToIndex(this.index + 1);
		}

		/* ===================== Mobile ===================== */
		setupMobile() {
			this.io = new IntersectionObserver(
				(entries) => {
					for (const e of entries) {
						const v = e.target.querySelector("video");
						if (e.isIntersecting) {
							e.target.classList.add("is-in");
							if (v && !this.fsCard) {
								const p = v.play();
								if (p && p.catch) p.catch(() => {});
							}
						} else if (v) {
							v.pause();
						}
					}
				},
				{ threshold: 0.35 },
			);
			this.cards.forEach((c) => this.io.observe(c));

			this.cardClick = this.cards.map((card) => {
				const h = () => (this.fsCard ? this.closeFs() : this.openFs(card));
				card.addEventListener("click", h);
				return h;
			});
			this.onFsClose = () => this.closeFs();
			this.fsClose.addEventListener("click", this.onFsClose);
		}

		openFs(card) {
			this.fsCard = card;
			this.cards.forEach((c) => {
				if (c !== card) {
					const v = c.querySelector("video");
					if (v) v.pause();
				}
			});
			card.classList.add("is-fs");
			this.fsClose.classList.add("is-on");
			document.documentElement.classList.add("ssc-lock");
			const v = card.querySelector("video");
			if (v) {
				const p = v.play();
				if (p && p.catch) p.catch(() => {});
			}
		}

		closeFs() {
			if (!this.fsCard) return;
			this.fsCard.classList.remove("is-fs");
			this.fsClose.classList.remove("is-on");
			document.documentElement.classList.remove("ssc-lock");
			this.fsCard = null;
		}

		/* ===================== Cleanup ===================== */
		teardown() {
			window.removeEventListener("scroll", this.onScroll);
			window.removeEventListener("resize", this.onResize);
			if (this.onWheel) window.removeEventListener("wheel", this.onWheel);
			if (this.onKey) window.removeEventListener("keydown", this.onKey);
			if (this.io) {
				this.io.disconnect();
				this.io = null;
			}
			if (this.cardClick) {
				this.cards.forEach((card, i) => card.removeEventListener("click", this.cardClick[i]));
				this.cardClick = null;
			}
			if (this.focusClose && this.onClose) this.focusClose.removeEventListener("click", this.onClose);
			if (this.fsClose && this.onFsClose) this.fsClose.removeEventListener("click", this.onFsClose);
			clearTimeout(this._lockTimer);
			this.closeFs();
			this.classList.remove("is-focused", "ssc--no-anim");
			this.focused = false;
			this._desktop = false;
			this.cards.forEach((c) => {
				c.classList.remove("is-dock", "is-in");
				c.style.transform = "";
				c.style.opacity = "";
				c.style.zIndex = "";
			});
		}
	}

	customElements.define("soulog-showcase-scroll", SoulogShowcaseScroll);
}
